import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as EmptyExercises } from "../assets/images/illustrations/3D/empty-exam.svg"
import { Each } from "../common/Each"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import ExerciseDialog from "../components/dialogs/ExerciseDialog"
import EmptyContent from "../components/EmptyContent"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import { DialogStatus } from "../enums"
import typo from "../typography.module.css"
import styles from "./Exercises.module.css"

const mergeCollapsedState = (previousState, nextIds, defaultCollapsed = true) => {
  const current = new Map(previousState.map((item) => [item.id, item.collapsed]))
  return nextIds.map((id) => ({
    id,
    collapsed: current.has(id) ? current.get(id) : defaultCollapsed,
  }))
}

const groupExercisesByCourseEdition = (exercises, t) => {
  const grouped = exercises.reduce((acc, exercise) => {
    const course = exercise?.module?.course
    const edition = exercise?.module?.edition

    const courseId = course?.id ?? "no-course"
    if (!acc[courseId]) {
      acc[courseId] = {
        course: {
          id: courseId,
          name: course?.name ?? t("exercises.title"),
          thumbnail: course?.thumbnail ?? null,
        },
        editionsMap: {},
      }
    }

    const editionId = edition?.id ?? `no-edition-${courseId}`
    if (!acc[courseId].editionsMap[editionId]) {
      acc[courseId].editionsMap[editionId] = {
        edition: {
          id: editionId,
          name: edition?.name ?? "Senza edizione",
        },
        exercises: [],
      }
    }

    acc[courseId].editionsMap[editionId].exercises.push(exercise)
    return acc
  }, {})

  return Object.values(grouped)
    .map((courseGroup) => ({
      ...courseGroup,
      editions: Object.values(courseGroup.editionsMap)
        .map((editionGroup) => ({
          ...editionGroup,
          exercises: [...editionGroup.exercises].sort((a, b) => {
            if (!a.deleted_at && b.deleted_at) return -1
            if (a.deleted_at && !b.deleted_at) return 1
            return (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
          })
        }))
        .sort((a, b) => (a.edition?.name ?? "").localeCompare(b.edition?.name ?? "", undefined, { sensitivity: "base" })),
    }))
    .sort((a, b) => (a.course?.name ?? "").localeCompare(b.course?.name ?? "", undefined, { sensitivity: "base" }))
}

const Exercises = () => {

  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [exercises, setExercises] = useState([])
  const [filteredExercises, setFilteredExercises] = useState([])

  const [loading, setLoading] = useState(false)
  const [currentExercise, setCurrentExercise] = useState(null)
  const [dialogTimeout, setDialogTimeout] = useState(null)
  const [courseCollapsableState, setCourseCollapsableState] = useState([])
  const [selectedEditionByCourse, setSelectedEditionByCourse] = useState({})
  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [selectedDeleteExercise, setSelectedDeleteExercise] = useState(null)
  const deleteCloseTimeoutRef = useRef(null)

  const groupedExercises = useMemo(() => groupExercisesByCourseEdition(filteredExercises, t), [filteredExercises, t])

  const syncCollapsedStateFromGroups = useCallback((groups) => {
    const courseIds = groups.map((group) => group.course.id)

    setCourseCollapsableState((prev) => mergeCollapsedState(prev, courseIds, true))
    setSelectedEditionByCourse((prev) => {
      const next = {}

      for (const group of groups) {
        const courseId = group.course.id
        const validEditionIds = group.editions.map((editionGroup) => editionGroup.edition.id)
        const previousSelection = prev[courseId]
        next[courseId] = validEditionIds.includes(previousSelection) ? previousSelection : null
      }

      return next
    })
  }, [])

  const getExercises = useCallback(async () => {
    setLoading(true);
    try {
      const exercises = await api.get("/teacher/exercises");
      setExercises(exercises);
      setFilteredExercises(exercises);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [])

  useEffect(() => {
    getExercises();
  }, [getExercises]);

  useEffect(() => {
    syncCollapsedStateFromGroups(groupedExercises)
  }, [groupedExercises, syncCollapsedStateFromGroups])

  useEffect(() => {
    if (!open) {
      setCurrentExercise(null)
    }
  }, [open])

  const onSave = useCallback(() => {
    getExercises()
    setDialogTimeout(setTimeout(() => setOpen(false), 4000))
  }, [getExercises])

  const onSearch = useCallback((text) => {
    if (exercises && exercises.length > 0) {
      if (text) {
        const filter = exercises.filter((s) => {
          const target = `${s.name} ${s.module?.name ?? ""} ${s.module?.course?.name ?? ""} ${s.module?.edition?.name ?? ""} ${s.lesson?.name ?? ""}`.toLowerCase().replaceAll(" ", "");
          const query = text ? text.toLowerCase().replaceAll(" ", "") : text
          return target.includes(query);
        });
        setFilteredExercises(filter);
      } else {
        setFilteredExercises(exercises);
      }
    }
  }, [exercises])

  const onEdit = useCallback((exercise) => {
    setCurrentExercise(exercise)
    setOpen(true)
  }, [])

  const deleteExercise = useCallback(async () => {
    if (!selectedDeleteExercise) {
      return
    }

    try {
      setAlertStatus(DialogStatus.Loading)
      await api.delete(`/teacher/exercises/${selectedDeleteExercise.id}`)
      await getExercises()
      setAlertStatus(DialogStatus.Success)
      deleteCloseTimeoutRef.current = setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
        setSelectedDeleteExercise(null)
      }, 1200)
    } catch (e) {
      console.error(e)
      setAlertStatus(DialogStatus.Error)
    }
  }, [getExercises, selectedDeleteExercise])

  const getMenuActions = useCallback((exercise) => {
    if (!exercise || exercise.deleted_at) {
      return []
    }

    return [
      {
        label: t("edit"),
        icon: EditIcon,
        style: { color: "var(--primary)" },
        onClick: () => onEdit(exercise),
      },
      {
        label: t("delete"),
        icon: DeleteIcon,
        style: { color: "var(--secondary)" },
        onClick: () => {
          setSelectedDeleteExercise(exercise)
          setOpenAlert(true)
        },
      },
    ]
  }, [onEdit, t])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{t("exercises.title")}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.content}>
              <div className={styles.header}>
                <div className={typo.title}>{t("exercises.title")}</div>


                <div className={styles.add}>
                  <Button
                    appearance="subtle"
                    onClick={() => setOpen(true)}
                  >
                    <AddIcon />
                    {t("exercises.add").toUpperCase()}
                  </Button>
                </div>
              </div>
              <TextInput
                onKeyUp={onSearch}
                type="search"
                placeholder={"Ricerca"}
                style={{ backgroundColor: "var(--background-secondary-color)" }} />
              <div className={styles.modules}>
                {
                  loading === true &&
                  <Each
                    of={[1, 2]}
                    render={() => (
                      <Card>
                        <div className={styles.courseSkeleton}>
                          <div className={styles.courseSkeletonHeader}>
                            <Skeleton type="rect" width="256px" height={"22px"} borderRadius={"8px"} />
                          </div>
                        </div>
                      </Card>
                    )}
                  />
                }

                {
                  loading === false && groupedExercises.length > 0 &&
                  <Each
                    of={groupedExercises}
                    render={(courseGroup) => {
                      const isCourseCollapsed = courseCollapsableState.find((item) => item.id === courseGroup.course.id)?.collapsed
                      const selectedEditionId = selectedEditionByCourse[courseGroup.course.id] ?? null
                      const selectedEditionGroup = selectedEditionId
                        ? courseGroup.editions.find((editionGroup) => editionGroup.edition.id === selectedEditionId)
                        : null
                      const exercisesToShow = selectedEditionGroup
                        ? selectedEditionGroup.exercises
                        : courseGroup.editions.flatMap((editionGroup) => editionGroup.exercises)

                      return (
                        <Card
                          title={
                            <div
                              className={styles.courseHeader}
                              onClick={() => {
                                setCourseCollapsableState((prev) => prev.map((item) =>
                                  item.id === courseGroup.course.id ? { ...item, collapsed: !item.collapsed } : item
                                ))
                              }}
                            >
                              {
                                courseGroup.course.thumbnail
                                  ? <img src={courseGroup.course.thumbnail} alt="" className={styles.courseThumbnail} />
                                  : <div className={styles.courseThumbnailFallback}>{courseGroup.course.name?.charAt(0) ?? "-"}</div>
                              }
                              <div className={styles.courseTitle}>{courseGroup.course.name}</div>
                            </div>
                          }
                        >
                          <ChevronIcon
                            className={`${styles.courseChevron} ${isCourseCollapsed ? styles.collapsed : ''}`}
                            onClick={() => {
                              setCourseCollapsableState((prev) => prev.map((item) =>
                                item.id === courseGroup.course.id ? { ...item, collapsed: !item.collapsed } : item
                              ))
                            }}
                          />

                          <div className={`${styles.courseBody} ${isCourseCollapsed ? styles.collapsed : ''}`}>
                            <div className={styles.editionFilters}>
                              <button
                                className={`${styles.editionFilter} ${selectedEditionId === null ? styles.selected : ''}`}
                                onClick={() => {
                                  setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: null }))
                                }}
                              >
                                Tutto
                              </button>
                              <Each
                                of={courseGroup.editions}
                                render={(editionGroup) => (
                                  <button
                                    className={`${styles.editionFilter} ${selectedEditionId === editionGroup.edition.id ? styles.selected : ''}`}
                                    onClick={() => {
                                      setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: editionGroup.edition.id }))
                                    }}
                                  >
                                    {editionGroup.edition.name}
                                  </button>
                                )}
                              />
                            </div>

                            <Table
                              appearance="minimal"
                              columns={[
                                { key: 'name', label: t("exercises.name"), type: 'string' },
                                { key: 'module_name', label: 'MODULO', type: 'custom' },
                                { key: 'lesson_name', label: t("exercises.lesson.label"), type: 'custom' },
                                { key: 'materials_count', label: t("exercises.materials"), type: 'number' },
                                { key: 'submit_count', label: t("exercises.submitNumber"), type: 'string' },
                                { key: 'expires_at', label: t("exercises.expiresAt"), type: 'datetime' },
                                { key: '_actions', label: 'Azioni', type: 'custom' },
                              ]}
                              elements={exercisesToShow.map((exercise) => ({
                                ...exercise,
                                module_name: {
                                  value: exercise.module?.name ?? '-',
                                  type: 'string',
                                  component: <div className={styles.clampedLabel}>{exercise.module?.name ?? '-'}</div>,
                                },
                                lesson_name: {
                                  value: exercise.lesson?.name ?? 'Nessuna',
                                  type: 'string',
                                  component: <div className={styles.clampedLabel}>{exercise.lesson?.name ?? 'Nessuna'}</div>,
                                },
                                materials_count: exercise.medias?.length ?? 0,
                                submit_count: `${exercise.users_exercises_count ?? 0} / ${exercise.users ?? 0}`,
                                _actions: {
                                  value: exercise.id,
                                  type: 'number',
                                  component: (
                                    <div className={styles.actionCell}>
                                      {!exercise.deleted_at && <MeatBallsMenu actions={getMenuActions(exercise)} />}
                                    </div>
                                  ),
                                },
                                href: `/exercises/${exercise.id}`,
                              }))}
                            />
                          </div>
                        </Card>
                      )
                    }}
                  />
                }

                {
                  loading === false && groupedExercises.length === 0 &&
                  <EmptyContent
                    Illustration={EmptyExercises}
                    message="Non ci sono ancora esercizi, creane uno nuovo per vederlo in lista"
                    imageStyle={{ width: "192px" }}
                  />
                }
              </div>
            </div>
            <ExerciseDialog
              open={open}
              onClose={() => {
                if (dialogTimeout) {
                  clearTimeout(dialogTimeout)
                  setDialogTimeout(null)
                }
                setOpen(false)
              }}
              exercise={currentExercise}
              onSave={onSave}
            />

            <AlertDialog
              open={openAlert}
              title={t("exercises.deleteTitle")}
              text={t("exercises.deleteText")}
              status={alertStatus}
              onClose={() => {
                setOpenAlert(false)
                setAlertStatus(DialogStatus.Default)
                setSelectedDeleteExercise(null)
                if (deleteCloseTimeoutRef.current) {
                  clearTimeout(deleteCloseTimeoutRef.current)
                }
              }}
              actions={[
                {
                  label: t("exercises.deleteConfirm").toUpperCase(),
                  onClick: deleteExercise,
                }
              ]}
            />
          </div>
        </div>
      </div>

    </HeaderFooterLayout>
  )
}


export default Exercises
